import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo } from "./Logo";

const links = [
  { id: "problem", label: "Problem" },
  { id: "solution", label: "Solution" },
  { id: "how", label: "How It Works" },
  { id: "reports", label: "Reports" },
  { id: "audience-value", label: "Audience" },
  { id: "contact", label: "Contact" },
];

export const Nav = ({ onScrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track section in view for active nav indicator
  useEffect(() => {
    const sections = links
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);
    if (!sections.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry closest to the top (highest intersection ratio near viewport top)
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) {
          setActive(visible[0].target.id);
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const go = (id) => {
    setOpen(false);
    onScrollTo?.(id);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-xl bg-paper/75 border-b border-ink/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("top")}
          className="flex items-center group"
        >
          <Logo tone="onLight" size="md" className="transition-transform duration-500 group-hover:-translate-y-0.5" />
        </button>

        <nav className="hidden lg:flex items-center gap-6">
          {links.map((l) => {
            const isActive = active === l.id;
            return (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className={`relative font-mono-cap transition-colors ${
                  isActive ? "text-ink" : "text-ink/60 hover:text-ink"
                }`}
              >
                {l.label}
                <span
                  aria-hidden
                  className={`absolute left-0 right-0 -bottom-1 h-px bg-teal origin-left transition-transform duration-500 ${
                    isActive ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-cta-demo"
            onClick={() => go("contact")}
            className="hidden lg:inline-flex btn-primary"
          >
            Request a Demo
            <span aria-hidden>→</span>
          </button>
          <button
            data-testid="nav-menu-toggle"
            className="lg:hidden p-2 border border-ink/20"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <div className="w-5 h-[1.5px] bg-ink mb-[5px]" />
            <div className="w-5 h-[1.5px] bg-ink" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="lg:hidden bg-paper border-t border-ink/10"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mnav-${l.id}`}
                  onClick={() => go(l.id)}
                  className={`font-mono-cap text-left transition-colors ${
                    active === l.id ? "text-teal" : "text-ink/80"
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mnav-cta-demo"
                onClick={() => go("contact")}
                className="btn-primary mt-2 w-full justify-center"
              >
                Request a Demo →
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
